import { SITE } from "@/lib/site";
import { ArrowRight, Phone } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A0A0A] grain"
    >
      {/* Background truck image */}
      <div className="absolute inset-0">
        <img
          src={SITE.assets.truck}
          alt="Coreline Heavy Haul Ram truck"
          className="w-full h-full object-cover object-center opacity-55"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
      </div>

      {/* Diagonal blue stroke */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-2/3 accent-stroke opacity-70" />
      <div className="absolute right-0 top-[30%] h-[2px] w-1/3 accent-stroke opacity-40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 lg:pt-48 pb-24">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-[2px] w-10 bg-[#1E5BFF]" />
            <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#1E5BFF]">
              Heavy Haul · Hotshot · Cross-Border
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black uppercase leading-[0.9] tracking-tighter fade-up"
          >
            Heavy Duty.
            <br />
            <span className="text-[#1E5BFF]">On Time.</span>
            <br />
            Every Time.
          </h1>

          <p
            data-testid="hero-subheading"
            className="mt-8 max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed"
          >
            Canadian-owned and operated. Coreline moves oversized loads, time-critical hotshot
            freight, and heavy iron across Western Canada and into the US — with the equipment,
            permits, and operators to do it right.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href="#quote"
              data-testid="hero-cta-quote"
              className="btn-slash group inline-flex items-center gap-3 bg-[#1E5BFF] hover:bg-[#3B72FF] text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-10 py-4"
            >
              Request a Quote
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={`tel:${SITE.contact.phone.replace(/\D/g, "")}`}
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-3 text-white/90 hover:text-white font-display font-bold uppercase tracking-[0.18em] text-sm border-b border-white/30 hover:border-[#1E5BFF] pb-1 transition-colors"
            >
              <Phone size={16} /> 24/7 Dispatch
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#1a1a1a] max-w-3xl">
            {SITE.stats.map((s) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-[#0A0A0A] px-5 py-6"
              >
                <div className="font-display text-3xl md:text-4xl font-black text-white tracking-tighter">
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom marquee strip */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-[#1a1a1a] bg-black/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-8 overflow-x-auto no-scrollbar">
          {[
            "Heavy Haul",
            "Hotshot Delivery",
            "Equipment Hauling",
            "Flatbed Transport",
            "Cross-Border CA / US",
            "24/7 Dispatch",
          ].map((t) => (
            <span
              key={t}
              className="whitespace-nowrap font-display text-xs font-bold uppercase tracking-[0.25em] text-white/60"
            >
              ◆ {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
